import React from 'react';
import classNames from 'classnames';
import { Observe } from 'rewire-core';
import { WithStyle, withStyles, FormView, IActionMenuItem } from 'rewire-ui';
import Button from '@material-ui/core/Button';
import { PopoverOrigin } from '@material-ui/core/Popover';
import { Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import EditIcon from '@material-ui/icons/EditOutlined';
import VisibilityIcon from '@material-ui/icons/Visibility';
import LockIcon from '@material-ui/icons/Lock';
import DraggableResizableDialog from '../ui/DraggableResizableDialog';
import OptionsActionMenu from '../ui/OptionsActionMenu';
import { makeStyles } from '../utility/styles';
import GeneratorCalendar, { TemplateCalendarEventComponent } from './GeneratorCalendar';
import { ITemplateCalendarEvent } from './GeneratorCalendarModel';
import mainPageModel from '../main-navigation/MainPageModel';
import generatorDialogModel, { GeneratorDialogModel } from './GeneratorDialogModel';
import TemplateDialog from './TemplateDialog';

const templateCalendarEventDraggableComponentStyles = (_theme: Theme) => ({
  eventWrapper: {
    cursor: 'grab',
    opacity: 1,
  },
  // isDragging: {
  //   opacity: 0.5,
  // },
});

interface ITemplateCalendarEventDraggableComponentProps {
  event: ITemplateCalendarEvent;
}

type TemplateCalendarEventDraggableComponentProps = WithStyle<
  ReturnType<typeof templateCalendarEventDraggableComponentStyles>,
  ITemplateCalendarEventDraggableComponentProps
>;

export const TemplateCalendarEventDraggableComponent = withStyles<ITemplateCalendarEventDraggableComponentProps, unknown>(
  templateCalendarEventDraggableComponentStyles,
  class extends React.Component<TemplateCalendarEventDraggableComponentProps> {
    constructor(props: TemplateCalendarEventDraggableComponentProps) {
      super(props);
    }

    handleDragStart = (event: ITemplateCalendarEvent) => (evt: React.DragEvent) => {
      generatorDialogModel.setDraggedEvent(event);
      evt.dataTransfer.setData('Text', event.id); // For firefox and Safari draggables to work properly.
    };

    render() {
      const { event, classes } = this.props;

      return (
        // <div draggable={true} onDragStart={this.handleDragStart(event)} className={classNames(classes.eventWrapper, generatorDialogModel.isDragging ? classes.isDragging : '')}>
        <div id={event.id} draggable={true} onDragStart={this.handleDragStart(event)} className={classNames(classes.eventWrapper)}>
          <TemplateCalendarEventComponent event={event} />
        </div>
      );
    }
  },
);

const useGeneratorCalendarEventsStyles = makeStyles((_theme: Theme) => ({
  templateCalendarEventsContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    // flexWrap: 'wrap',
    width: '100%',
    margin: '10px 0px',
    fontSize: '0.85em',
    maxHeight: '110px',
    overflowX: 'auto',
  },
  eventMenuButton: {
    flex: '0 0 auto',
    margin: '0px 5px 10px',
    padding: '0px',
    lineHeight: '1.2',
    fontSize: 'inherit',
    fontWeight: 'normal',
  },
}));

export const GeneratorCalendarEventsList = ({ model, classes }: { model: GeneratorDialogModel; classes?: any }) => {
  const calendarClasses = useGeneratorCalendarEventsStyles({ classes });
  const anchorOrigin: PopoverOrigin = { vertical: 'bottom', horizontal: 'left' };
  const transformOrigin: PopoverOrigin = { vertical: 'top', horizontal: 'left' };

  const handleEditTemplateClick = (templateId: string) => () => {
    model.showTemplateDialog(templateId);
  };

  // const handleDuplicateTemplateClick = (templateId: string) => () => {
  //   model.showDuplicateTemplateDialog(templateId);
  // }

  const handleDeleteTemplateClick = (templateId: string) => () => {
    model.deleteTemplate(templateId);
  };

  return (
    <div className={calendarClasses.templateCalendarEventsContainer}>
      <Observe
        render={() =>
          model.templateCalendarEvents.map((templateCalendarEvent: ITemplateCalendarEvent) => {
            const templateOptionsMenuItems: IActionMenuItem[] = [
              {
                name: 'editTemplate',
                title: mainPageModel.canEditTemplates ? 'Edit Shift Type' : 'View Shift Type',
                icon: mainPageModel.canEditTemplates ? EditIcon : VisibilityIcon,
                onClick: handleEditTemplateClick(templateCalendarEvent.templateId!),
                closeOnClick: true,
              },
              // {name: 'duplicateTemplate', title: 'Duplicate Shift Type', icon: FileCopyIcon, onClick: handleDuplicateTemplateClick(templateCalendarEvent.templateId!),                                             closeOnClick: true},
              {
                name: 'deleteTemplate',
                title: 'Delete Shift Type',
                icon: DeleteIcon,
                onClick: handleDeleteTemplateClick(templateCalendarEvent.templateId!) || undefined,
                closeOnClick: true,
                disabled: () => !mainPageModel.canDeleteTemplates || !!templateCalendarEvent.locked,
              },
            ];

            return (
              <OptionsActionMenu
                key={templateCalendarEvent.id}
                title="Shift Type Options"
                classes={{ menuButton: calendarClasses.eventMenuButton }}
                items={templateOptionsMenuItems}
                getContentAnchorEl={null}
                anchorOrigin={anchorOrigin}
                transformOrigin={transformOrigin}
                buttonProps={{ tabIndex: -1 }}
                buttonContent={
                  model.viewOnly || model.locked ? (
                    <div>
                      <TemplateCalendarEventComponent event={templateCalendarEvent} />
                    </div>
                  ) : (
                    <TemplateCalendarEventDraggableComponent event={templateCalendarEvent} />
                  )
                }
              />
            );
          })
        }
      />
    </div>
  );
};

const useGeneratorDialogContentsStyles = makeStyles((theme: Theme) => ({
  form: {},
  content: {
    margin: '0px 16px 15px 16px !important',
    '& > div': {
      display: 'flex',
      flex: 1,
    },
  },
  repeatDaysContainer: {
    display: 'flex',
    margin: '0px 16px 10px 16px',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  repeatEveryXDays: {
    width: '150px',
  },
  templatesContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    border: `1px solid ${theme.palette.neutral.main}`,
    margin: '0px 16px 10px 16px',
    padding: '10px',
  },
  templatesInputsContainer: {
    width: '100%',
    marginTop: '5px',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  templatesInputsInput: {
    marginRight: '10px',
    '&:last-child': {
      marginRight: '0px',
    },
  },
  addIcon: {
    marginRight: '5px',
    marginLeft: '-5px',
  },
  addTemplateButton: {
    textTransform: 'none',
  },
  calendarContainer: {
    border: `1px solid ${theme.palette.neutral.main}`,
    margin: '0px 16px',
    padding: '10px',
  },
}));

export const GeneratorDialogContents = ({ model, classes }: { model: GeneratorDialogModel; classes?: any }) => {
  const contentsClasses = useGeneratorDialogContentsStyles({ classes });

  React.useEffect(() => {
    const dragItems = document.querySelectorAll('[draggable=true]');

    for (let i = 0; i < dragItems.length; i++) {
      dragItems[i].addEventListener('dragstart', (evt: DragEvent) => {
        // store the ID of the element, and collect it on the drop later on
        evt!.dataTransfer!.setData('Text', dragItems[i].id);
      });
    }
  }, []);

  const handleAddTemplateClick = () => {
    model.showTemplateDialog();
  };

  const handleDragOver = (evt: DragEvent) => {
    // always true, just a placeholder for future potential development
    if (model.canDropFromOutside()) {
      evt.preventDefault();
    }
  };

  return (
    <Observe
      render={() => (
        <>
          <FormView className={contentsClasses.form} form={model.form} onSubmit={model.actionFn('save')}>
            <div className={contentsClasses.repeatDaysContainer}>
              <model.form.field.code.Editor className="span1" />
              <model.form.field.name.Editor className="span1" />
            </div>
            <div className={contentsClasses.repeatDaysContainer}>
              <Typography variant="body2">Repeat pattern every X days:</Typography>
              <model.form.field.repeatEveryXDays.Editor className={contentsClasses.repeatEveryXDays} />
            </div>
            <div className={contentsClasses.templatesContainer}>
              <div className={contentsClasses.templatesInputsContainer}>
                <model.form.field.calendarStartDate.Editor className={contentsClasses.templatesInputsInput} />
                <model.form.field.templateCodeSearch.Editor className={contentsClasses.templatesInputsInput} />
              </div>
              <Typography variant="body2">Select shift types - drop to calendar date to add to pattern</Typography>
              <GeneratorCalendarEventsList model={model} />
              <Button
                variant="contained"
                className={contentsClasses.addTemplateButton}
                disabled={!mainPageModel.canAddTemplates}
                onClick={handleAddTemplateClick}
              >
                <AddIcon className={contentsClasses.addIcon} /> Add Shift Type
              </Button>
            </div>
          </FormView>
          <div className={contentsClasses.calendarContainer}>
            <Observe
              render={() => (
                <GeneratorCalendar
                  model={model.generatorCalendarModel}
                  onDropFromOutside={model.onDropFromOutside}
                  onDragOver={handleDragOver}
                />
              )}
            />
          </div>
          <TemplateDialog />
        </>
      )}
    />
  );
};

const styles = (_theme: Theme) => ({
  dialogHeading: {
    alignItems: 'stretch',
  },
  dialogTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dialogTitleLabel: {
    display: 'flex',
    alignItems: 'center',
  },
  lockIcon: {
    marginLeft: '10px',
  },
});

interface IGeneratorDialogProps {}

type GeneratorDialogProps = WithStyle<ReturnType<typeof styles>, IGeneratorDialogProps>;

class GeneratorDialog extends React.PureComponent<GeneratorDialogProps> {
  optionsActionMenuItems: IActionMenuItem[];

  constructor(props: GeneratorDialogProps) {
    super(props);

    this.optionsActionMenuItems = [
      // {name: 'duplicateGenerator', title: 'Duplicate Shift Pattern', icon: FileCopyIcon, onClick: this.handleDuplicateGeneratorClick, closeOnClick: true, disabled: () => generatorDialogModel.hasChanges()},
      {
        name: 'deleteGenerator',
        title: 'Delete Shift Pattern',
        icon: DeleteIcon,
        onClick: this.handleDeleteGeneratorClick,
        closeOnClick: true,
        disabled: () => generatorDialogModel.locked || generatorDialogModel.isNew || !generatorDialogModel.canDelete,
      },
    ];
  }

  componentWillUnmount() {
    generatorDialogModel.deactivate();
    generatorDialogModel.reset();
  }

  // handleDuplicateGeneratorClick = () => {
  //   generatorDialogModel.showDuplicateGeneratorDialog();
  // }

  handleDeleteGeneratorClick = () => {
    generatorDialogModel.deleteGenerator();
  };

  renderTitle = (): JSX.Element => {
    const { classes } = this.props;

    return (
      <div className={classes.dialogTitleContainer}>
        <div className={classes.dialogTitleLabel}>
          {generatorDialogModel.title}
          {generatorDialogModel.locked && <LockIcon className={classes.lockIcon} />}
        </div>
        {!generatorDialogModel.isNew && (
          <div
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          >
            <OptionsActionMenu title="Shift Pattern Options" tooltip="Shift Pattern Options" items={this.optionsActionMenuItems} />
          </div>
        )}
      </div>
    );
  };

  render() {
    const { classes } = this.props;

    return (
      <Observe
        render={() => (
          <>
            <DraggableResizableDialog
              defaultWidth="sm"
              classes={{ heading: classes.dialogHeading }}
              DialogProps={{ dialog: generatorDialogModel, title: this.renderTitle, actions: ['cancel', 'save'] }}
              DraggableResizableBoxProps={{ minWidth: '430px', minHeight: '588px' }}
            >
              <GeneratorDialogContents model={generatorDialogModel} />
            </DraggableResizableDialog>
          </>
        )}
      />
    );
  }
}

export default withStyles<IGeneratorDialogProps, unknown>(styles, GeneratorDialog);
